import { Employee, Practitioner } from '@customTypes/Practitioner'
import { Availabilities, Availability, TimeSlots } from '@customTypes/Availabilities'
import { formatInTimeZone } from 'date-fns-tz'
import { Appointment, Booking, BookingV2, ExistingPatientBooking, RecallBookingInfo } from '@customTypes/Booking'
import { Gender } from '@customTypes/Generic'
import { isEmpty } from 'lodash'

export const getPractitionersFromEmployees = (employees: Employee[]): Practitioner[] =>
  employees.map(getPractitionerFromEmployee)

export const getPractitionerFromEmployee = ({
  referenceId,
  name,
  imgUrl,
  languages,
  countryFlags,
  firstAvailableSlot,
  gender,
  rating,
  positions,
  displayRole,
  specialisations,
}: Employee): Practitioner => ({
  referenceId,
  name,
  imageUrl: imgUrl,
  languages: isEmpty(countryFlags) ? languages : countryFlags,
  nextAvailable: firstAvailableSlot,
  gender,
  rating,
  positions,
  displayRole,
  specialisations,
})

export const getAvailabilitiesFromTimeSlots = (timeSlots: TimeSlots[]): Availabilities[] => {
  const availabilities: Availabilities[] = timeSlots.map((timeSlot) => ({
    date: timeSlot.date,
    availabilities: timeSlot.slots.map(
      (slot): Availability => ({
        start: formatInTimeZone(new Date(slot.start), 'Europe/Berlin', 'HH:mm:ss'), // In new endpoint we have full DateTime, and we use only time HH:mm:ss
        maxDurationInMinutes: slot.maxDurationInMin,
        practitionerReferenceIds: slot.employeeReferenceIds,
        originalStartTimestamp: slot.start,
      })
    ),
  }))

  return availabilities
}

export const getAppointmentFromBookingData = (bookingData: BookingV2): Appointment => {
  const booking = bookingData.booking

  const appointment: Appointment = {
    clinicReferenceId: bookingData.clinicId,
    dataPrivacyAccepted: true,
    email: booking?.patient?.correspondence?.email || booking?.contactPerson?.correspondence.email,
    patient: {
      gender: booking?.patient?.data.gender as Gender,
      firstName: booking?.patient?.data.firstName,
      lastName: booking?.patient?.data.lastName,
      birthdate: booking?.patient?.birthdate,
    },
    insuranceType: booking?.patient?.insuranceType,
    insuranceHolder: booking?.insuranceHolder,
    patientType: booking?.patientType,
    patientUnder18Years: booking?.patient?.child || false,
    phoneNumber: booking?.patient?.correspondence?.phoneNumber || booking?.contactPerson?.correspondence.phoneNumber,
    start: booking?.start,
    treatmentBundleReferenceId: booking?.treatmentBundleTypeReferenceId,
    employeeReferenceId: booking?.preferredPractitionerReferenceId,
    creationSource: booking?.creationSource,
    creationEvent: booking?.creationEvent,
    phoneVerificationCode: booking?.phoneVerificationCode,
    additionalAppointmentHints: booking?.additionalAppointmentHints,
  }

  if (booking?.contactPerson) {
    appointment.contactPerson = {
      firstName: booking?.contactPerson?.data.firstName,
      lastName: booking?.contactPerson?.data.lastName,
      gender: booking?.contactPerson?.data.gender as Gender,
    }
  }

  return appointment
}

export const getRecallBookingFromBookingData = (bookingData: Booking): ExistingPatientBooking => {
  const booking = bookingData.booking as RecallBookingInfo

  return {
    clinicReferenceId: bookingData.clinicId,
    employeeReferenceId: booking.preferredPractitionerReferenceId,
    patientReferenceId: booking.customerId,
    treatmentBundleReferenceId: booking.treatmentBundleTypeReferenceId,
    dataPrivacyAccepted: true,
    start: booking?.start,
    creationSource: booking.creationSource,
    creationEvent: booking.creationEvent,
  }
}
